"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { inventoryService } from "../../../../services/inventoryService/index.js";
import { normalizeProductApi } from "../../../../services/productService/normalize.js";
import { normalizeProductSummaryApi } from "../../../../services/productService/normalizePCP/normalize.js";
import { normalizeProductSummaryApiV2 } from "../../../../services/productService/normalizePCP_V2/normalize.js";
import { normalizeGRSData } from "../../../../services/searchService/GRS/normalizeGRSData.js";
import { normalizeLwData } from "../../../../services/searchService/LucidWorks/normalizeLwData.js";
import { useProductTileConfig } from "../../../../utils/ProductTileConfigProvider/ProductTileConfigProviderClient.js";
import { isBD } from "../../../../utils/businessType.lib.js";
import { isFeatureEnabledForApplication } from "../../../../utils/contentstack/lib.js";
import { ProductTileProvider } from "../../../../utils/productTile/ProductTileProvider.js";
import { actionButtonControlsCheck } from "../../../../utils/productTile/utils.js";
import { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { Link, Text } from "@nxt-costco-com/forge-components";
import { Analytics } from "../../../Analytics/index.js";
import { ONE, KEY_PRODUCT_CLICK, KEY_SEARCH_CLICK, CATEGORY_PAGE_TYPE } from "../../../Analytics/constants.js";
import "../../../Chrome/BrowseContext/ContextProvider.js";
import { useBrowseContext } from "../../../Chrome/BrowseContext/hook.js";
import { Variants, COMPONENT_NAME } from "../constants.js";
import { useAuth } from "../../../../hooks/auth/useAuth/index.js";
import { CardBody } from "./CardBody/index.js";
import { CardFooter } from "./CardFooter/index.js";
import { getTileContainerStyle, getAnalyticsComponent, getAnalyticsTrackedData, getDoNotTrack, getDataSource, checkIsAd, getSponsoredBadgeStyle } from "./utils.js";
import { ProductTileSkeleton } from "./view.skeleton.js";
import { linkWrapperStyles } from "./styles.js";
function ProductTileUI(props) {
  var _a, _b, _c;
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(
    false
  );
  const [normalizedData, setNormalizedData] = useState(null);
  const [pdpNavigationUrl, setPdpNavigationUrl] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [loginFunctionsLoaded, setLoginFunctionsLoaded] = useState(false);
  const { isUserSignedIn, isLoading: isAuthUtilsLoading } = useAuth();
  useEffect(() => {
    if (!isAuthUtilsLoading) {
      isUserSignedIn().then((result) => {
        setIsUserLoggedIn(result);
      }).finally(() => {
        setLoginFunctionsLoaded(true);
      });
    }
  }, [isAuthUtilsLoading, isUserSignedIn]);
  const cookieContext = useBrowseContext();
  const { deliveryLocation } = cookieContext;
  const { isDomainInclusionDisabledByKeyword, keyword } = useProductTileConfig();
  const {
    variant,
    inventoryAware,
    configData,
    productApiResponse,
    locale,
    showSkeleton = false,
    sx,
    maxImageHeight,
    showReviews,
    showActionButton,
    isHorizontal,
    lwData,
    grsData,
    isSponsored,
    productTileMaxLineNumber,
    minProductTileImageWidth,
    maxProductTileImageWidth,
    hideReviewStarValue,
    showCompareProduct,
    criteoDataSourceAttr,
    ariaLinkDescribedBy,
    pageType,
    dataPrivate,
    doFocus,
    enableAnalytics = true,
    tileIndex,
    showMultiVariantsAvailability,
    sponsoredDisplayVariant = "default",
    product_tile_data_source
  } = props;
  const isInformative = variant === Variants.INFORMATIVE;
  const {
    featureToggleConfig,
    translationsConfig,
    inventoryApiConfig,
    fsaConfig
  } = configData || {};
  const enablePromoBreakdown = isFeatureEnabledForApplication(
    (_a = featureToggleConfig == null ? void 0 : featureToggleConfig.promoBreakdown) == null ? void 0 : _a.featureOn,
    (_b = featureToggleConfig == null ? void 0 : featureToggleConfig.promoBreakdown) == null ? void 0 : _b.enabledApplications
  );
  const { inventoryItemNumbers, productApiResponseData } = productApiResponse || {};
  const stringifiedInventoryItemNumbers = inventoryItemNumbers == null ? void 0 : inventoryItemNumbers.map(String);
  const { actionButtonControls } = translationsConfig || {};
  const { requiresDeliveryZipcode } = actionButtonControls || {};
  const isBusinessDeliverySite = isBD() ?? false;
  const isMeetingActionButtonRequirements = showActionButton && actionButtonControlsCheck(
    requiresDeliveryZipcode,
    isBusinessDeliverySite,
    deliveryLocation
  );
  const isMeetingPricingGroupRequirements = isBusinessDeliverySite && !deliveryLocation.zipCode ? false : true;
  const [doFocusCounter, setDoFocusCounter] = useState(0);
  const focusRef = useCallback(
    (node) => {
      if (node && doFocus && doFocus > doFocusCounter) {
        node.focus();
        setDoFocusCounter(doFocus);
      }
    },
    [doFocus, doFocusCounter]
  );
  useEffect(() => {
    var _a2, _b2, _c2, _d;
    const abortController = new AbortController();
    const signal = abortController.signal;
    const callProductSummaryAPI = ((_b2 = (_a2 = configData == null ? void 0 : configData.featureToggleConfig) == null ? void 0 : _a2.callProductSummaryAPI) == null ? void 0 : _b2.isEnabled) || product_tile_data_source === "product_summary_api";
    const fetchParsedProductData = async () => {
      var _a3, _b3;
      let inventoryResponseData = productApiResponse == null ? void 0 : productApiResponse.inventoryResponseData;
      if (inventoryAware && !(productApiResponse == null ? void 0 : productApiResponse.hasBatchInventoryResponseData)) {
        const inventoryResponse = await inventoryService(
          stringifiedInventoryItemNumbers,
          inventoryApiConfig,
          signal,
          cookieContext
        );
        if (!(inventoryResponse == null ? void 0 : inventoryResponse.ok)) {
          console.error("Inventory Api failed", inventoryResponse);
        } else {
          inventoryResponseData = await inventoryResponse.json();
        }
      }
      const { warehousePriceOnline } = (configData == null ? void 0 : configData.featureToggleConfig) || {};
      let normalizedDataObj;
      if (callProductSummaryAPI && (warehousePriceOnline == null ? void 0 : warehousePriceOnline.isEnabled)) {
        normalizedDataObj = normalizeProductSummaryApiV2(
          {
            inventoryResponseData,
            productApiResponseData
          },
          {
            ...configData,
            pillBadgeConfig: {
              ...configData == null ? void 0 : configData.pillBadgeConfig,
              isDomainInclusionDisabledByKeyword
            }
          },
          Boolean(isUserLoggedIn),
          cookieContext,
          keyword,
          isSponsored,
          void 0,
          locale
        );
      } else {
        const normalizeFunc = callProductSummaryAPI ? normalizeProductSummaryApi : normalizeProductApi;
        normalizedDataObj = normalizeFunc(
          {
            inventoryResponseData,
            productApiResponseData
          },
          {
            ...configData,
            pillBadgeConfig: {
              ...configData == null ? void 0 : configData.pillBadgeConfig,
              isDomainInclusionDisabledByKeyword
            }
          },
          Boolean(isUserLoggedIn),
          cookieContext,
          keyword,
          isSponsored
        );
      }
      setNormalizedData(
        callProductSummaryAPI ? normalizedDataObj : normalizedDataObj == null ? void 0 : normalizedDataObj[0]
      );
      setPdpNavigationUrl(
        callProductSummaryAPI ? (_a3 = normalizedDataObj == null ? void 0 : normalizedDataObj.productInfoData) == null ? void 0 : _a3.productLinkUrl : (_b3 = normalizedDataObj == null ? void 0 : normalizedDataObj[0]) == null ? void 0 : _b3.productInfoData.productLinkUrl
      );
      setIsLoading(false);
    };
    if (!normalizedData && loginFunctionsLoaded) {
      if (!lwData && !grsData) {
        fetchParsedProductData();
      } else {
        let normalizedDataObj;
        if (grsData) {
          normalizedDataObj = (_c2 = normalizeGRSData(
            [grsData],
            {
              ...configData,
              pillBadgeConfig: {
                ...configData == null ? void 0 : configData.pillBadgeConfig,
                isDomainInclusionDisabledByKeyword
              }
            },
            Boolean(isUserLoggedIn),
            cookieContext,
            showMultiVariantsAvailability
          )) == null ? void 0 : _c2[0];
        } else {
          normalizedDataObj = (_d = normalizeLwData(
            [lwData],
            {
              ...configData,
              pillBadgeConfig: {
                ...configData == null ? void 0 : configData.pillBadgeConfig,
                isDomainInclusionDisabledByKeyword
              }
            },
            Boolean(isUserLoggedIn),
            cookieContext,
            keyword,
            showMultiVariantsAvailability
          )) == null ? void 0 : _d[0];
        }
        setNormalizedData(normalizedDataObj);
        setPdpNavigationUrl(
          normalizedDataObj == null ? void 0 : normalizedDataObj.productInfoData.productLinkUrl
        );
        setIsLoading(false);
      }
    }
    return () => {
      abortController.abort();
    };
  }, [
    keyword,
    inventoryAware,
    configData,
    isDomainInclusionDisabledByKeyword,
    inventoryApiConfig,
    stringifiedInventoryItemNumbers,
    productApiResponseData,
    lwData,
    normalizedData,
    cookieContext,
    grsData,
    isUserLoggedIn,
    loginFunctionsLoaded,
    showMultiVariantsAvailability,
    isSponsored,
    productApiResponse == null ? void 0 : productApiResponse.inventoryResponseData
  ]);
  if (showSkeleton || isLoading)
    return /* @__PURE__ */ jsx(
      ProductTileSkeleton,
      {
        isHorizontal,
        minProductTileImageWidth
      }
    );
  if (!normalizedData) {
    return null;
  }
  if (!normalizedData.isPublished) {
    console.log(
      "Unpublished product tile",
      normalizedData.productInfoData.partNumber
    );
  }
  const { productInfoData, isFsaEligible, isChdiEligible } = normalizedData;
  const productTitleId = `${COMPONENT_NAME}_${productInfoData.partNumber}_title`;
  const translations = translationsConfig == null ? void 0 : translationsConfig.translations;
  const tileContainerStyles = getTileContainerStyle({
    sx,
    isSponsored,
    sponsoredDisplayVariant
  });
  const sponsoredBadgeStyle = getSponsoredBadgeStyle(sponsoredDisplayVariant);
  const isAd = checkIsAd({ isSponsored, criteoDataSourceAttr });
  const doNotTrack = getDoNotTrack({
    dataPrivate,
    isFsaEligible,
    isChdiEligible,
    enableAnalytics
  });
  const analyticsComponent = getAnalyticsComponent(
    productInfoData,
    normalizedData.isCostcoTravel
  );
  const analyticsTrackedData = getAnalyticsTrackedData(
    productInfoData,
    normalizedData.isCostcoTravel
  );
  return /* @__PURE__ */ jsx(
    Analytics,
    {
      analyticData: {
        adItem: { url: pdpNavigationUrl },
        component: analyticsComponent,
        doNotTrack,
        trackedData: analyticsTrackedData
      },
      children: /* @__PURE__ */ jsx(
        ProductTileProvider,
        {
          value: {
            isInformative,
            locale,
            normalizedData,
            productTitleId,
            translationsConfig,
            isSignedIn: isUserLoggedIn
          },
          children: /* @__PURE__ */ jsxs(
            Box,
            {
              "data-source": getDataSource(isAd),
              sx: tileContainerStyles,
              "data-testid": `${COMPONENT_NAME}_${productInfoData.partNumber}`,
              "data-tile-index": tileIndex,
              "data-private": doNotTrack,
              role: "group",
              children: [
                /* @__PURE__ */ jsx(
                  Analytics,
                  {
                    analyticData: {
                      doNotTrack: !enableAnalytics,
                      trackedData: [
                        {
                          key: pageType === CATEGORY_PAGE_TYPE ? KEY_PRODUCT_CLICK : KEY_SEARCH_CLICK,
                          value: ONE
                        }
                      ]
                    },
                    children: /* @__PURE__ */ jsx(
                      Link,
                      {
                        "aria-describedby": ariaLinkDescribedBy ? ariaLinkDescribedBy : null,
                        href: pdpNavigationUrl || "",
                        sx: linkWrapperStyles,
                        ref: focusRef,
                        children: /* @__PURE__ */ jsx(Box, { component: "span", sx: visuallyHidden, children: productInfoData.productName })
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsx(
                  CardBody,
                  {
                    isCriteo: isAd,
                    fsaConfig,
                    hideReviewStarValue,
                    isCriteoCarousel: criteoDataSourceAttr,
                    isHorizontal,
                    maxImageHeight,
                    maxProductTileImageWidth,
                    minProductTileImageWidth,
                    productTileMaxLineNumber: ((_c = translationsConfig == null ? void 0 : translationsConfig.product_display_style) == null ? void 0 : _c.description_max_line_numbers) || productTileMaxLineNumber,
                    setPdpNavigationUrl,
                    showReviews: Boolean(showReviews),
                    showPricing: isMeetingPricingGroupRequirements,
                    showActionButton,
                    cookieContext,
                    enablePromoBreakdown
                  }
                ),
                isInformative && /* @__PURE__ */ jsx(
                  CardFooter,
                  {
                    pageType,
                    pdpNavigationUrl,
                    showActionButton: isMeetingActionButtonRequirements,
                    showCompareProduct
                  }
                ),
                isSponsored && /* @__PURE__ */ jsx(Text, { sx: sponsoredBadgeStyle, variant: "t7", children: translations.sponsoredlabel })
              ]
            }
          )
        }
      )
    }
  );
}
ProductTileUI.displayName = COMPONENT_NAME;
export {
  ProductTileSkeleton,
  ProductTileUI
};
